import { Link } from "react-router-dom";

import { relativeTime, useVesselPath } from "@/lib/hooks";
import type { CyaMeasurementDue, CyaProgress } from "../types";

/** Progress of a CYA reduction campaign.
 *
 * Renders nothing when CYA is at or below the goal — a campaign that is not
 * running should not occupy space on a dashboard used one-handed in sunlight.
 *
 * The bar shows distance travelled against distance remaining, which is the
 * honest framing: each drain removes the same *fraction* of the water, so the
 * later ones move the number least. A "3 of 7 drains" progress bar would imply
 * a linear journey and overstate how close the end is.
 *
 * **Every figure on this card comes from the last CYA test**, so a drain logged
 * since that test leaves all of them describing water the pool no longer holds.
 * When the server says so, the next-drain instruction is replaced rather than
 * annotated: "drain 12 in and refill" is the one thing on here that spends
 * water, and a footnote under an instruction is not what stops someone
 * following it. The numbers themselves stay exactly as they are — they were
 * measured, and re-deriving them from the model's own estimate would be the
 * app checking its predictions against itself.
 */
export function CyaProgressCard({ progress }: { progress: CyaProgress }) {
  if (!progress.active || progress.current_cya === null) return null;

  const start = progress.starting_cya === null ? null : Number(progress.starting_cya);
  const current = Number(progress.current_cya);
  const goal = Number(progress.goal_cya);

  // Guard against a start at or below the goal, which would divide by zero.
  const span = start !== null && start > goal ? start - goal : null;
  const done = span === null ? 0 : Math.min(100, Math.max(0, ((start! - current) / span) * 100));

  const due = progress.measurement_due;
  const stages = progress.remaining?.stages ?? [];
  const nextStage = stages[0];
  const outcomes = progress.outcomes;
  const lastChecked = [...outcomes].reverse().find((o) => o.agrees !== null);
  // "Last drain" is only true while the drain it judged is the newest one. Once
  // a later drain is logged and not yet tested, the same phrase points at the
  // wrong event — and it does so on the exact card that is asking for the test
  // that would judge the newer one.
  const checkedIsLatest = lastChecked !== undefined && lastChecked === outcomes.at(-1);

  return (
    <section className="card px-4 py-3.5">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-sm font-medium">Lowering CYA</h2>
        <p className="unit">goal {goal} ppm</p>
      </div>

      <div className="mt-3 flex items-baseline gap-2">
        {/* Left in reagent colour even with a drain outstanding. It is still
            the last figure anybody read off a kit, it is still what the
            chlorine floor is derived from (ADR 0010 carries it through a
            dilution deliberately), and the panel below shows it the same way.
            Greying it here would be this screen disagreeing with itself; the
            qualification is carried in words underneath instead. */}
        <span className="figure text-3xl" style={{ color: "var(--color-reagent-cyanuric)" }}>
          {current}
        </span>
        <span className="unit">ppm</span>
        {span !== null && (
          <span className="unit ml-auto">
            down {Math.round(start! - current)} from {Math.round(start!)}
          </span>
        )}
      </div>

      {span !== null && (
        <div
          className="mt-2 h-1.5 overflow-hidden rounded-full bg-(--color-border-subtle)"
          role="progressbar"
          aria-valuenow={Math.round(done)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`CYA reduction progress toward ${goal} ppm`}
        >
          <div
            className="h-full rounded-full transition-[width] duration-500"
            style={{ width: `${done}%`, background: "var(--color-reagent-cyanuric)" }}
          />
        </div>
      )}

      {due ? (
        <MeasurementDueNote due={due} />
      ) : (
        nextStage && (
          <p className="mt-3 text-sm">
            Next: drain{" "}
            <strong className="figure">
              {nextStage.inches ? `${nextStage.inches} in` : `${nextStage.gallons} gal`}
            </strong>{" "}
            and refill — takes it to about{" "}
            <strong className="figure">{nextStage.cya_after}</strong> ppm.
          </p>
        )
      )}

      <p className="unit mt-1.5">
        {/* Dated while a test is outstanding. The stage count is derived from
            the measured figure like everything else, so presenting it flat
            beneath a note explaining that the figure predates a drain would put
            two readings of the same fact on one card. */}
        {stages.length > 0 && (
          <>
            {stages.length} more at this depth
            {due && " as things stood"} ·{" "}
          </>
        )}
        {progress.drains_logged} logged so far
      </p>

      {/* The self-check. Only shown once there is a drain it could actually
          judge — an empty "unverified" row would be noise. */}
      {lastChecked && (
        <p className="unit mt-2 border-t border-(--color-border-subtle) pt-2">
          <span style={{ color: lastChecked.agrees ? undefined : "var(--color-status-warn)" }}>
            {checkedIsLatest
              ? "Last drain"
              : `The drain ${relativeTime(lastChecked.occurred_at)}`}{" "}
            {lastChecked.agrees ? "matched the estimate" : "missed the estimate"}
          </span>
          {" — "}
          {lastChecked.note}
        </p>
      )}

      {progress.remaining && (
        <p className="unit mt-2">
          Free chlorine floor drops from {progress.remaining.fc_minimum_now} to{" "}
          {progress.remaining.fc_minimum_after} ppm at goal.
        </p>
      )}
    </section>
  );
}

/**
 * What the campaign is waiting on, in the place the next drain would have been.
 *
 * Verdict first and working after — the house rule this dashboard applies in
 * `SaturationCard` and `ReadingDueCard`, and it matters more here than usual
 * because the reasoning runs to several sentences and the instruction is one.
 *
 * The predicted level is shown, and shown as a prediction. That line is worth
 * defending: it is the difference between walking out to the pool knowing to
 * dilute the sample and walking out to record a "100" that is really 131. What
 * it must never become is an input — the whole point of the measurement is to
 * check it, so nothing on this card is sized from it.
 */
function MeasurementDueNote({ due }: { due: CyaMeasurementDue }) {
  const vp = useVesselPath();

  return (
    <div className="mt-3 rounded-lg border border-(--color-border-strong) px-3 py-2.5">
      <p className="text-sm font-medium" style={{ color: "var(--color-status-warn)" }}>
        {due.headline}
      </p>

      {/* Both timestamps, because the gap between them is the whole finding and
          neither one says it alone. Relative rather than clock time: it reads
          better on a phone and cannot come out an hour wrong. */}
      <p className="unit mt-1">
        drained {relativeTime(due.last_drain_at)} · CYA last measured{" "}
        {relativeTime(due.measured_at)}
      </p>

      <p className="mt-1.5 text-sm text-(--color-ink-muted)">{due.note}</p>

      {due.predicted_cya !== null && (
        <p className="unit mt-2">
          estimate <span className="figure">{due.measured_cya}</span> →{" "}
          <span className="figure">{due.predicted_cya}</span> ppm, unconfirmed
        </p>
      )}

      <Link to={vp("/log")} className="btn btn-primary mt-3 w-full text-sm">
        Log a reading
      </Link>
    </div>
  );
}
